import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Calendar, History, Star, Wallet, FileText,
} from "lucide-react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { RootState } from "../../../../redux/store";

import CleaningBookings    from "./CleaningBookings";
import CleaningReviewPreview     from "./CleaningReviewPreview";

import logo from "../../../../assets/app/logo.png";

/* ── green design tokens ── */
const T = {
  bg:            "#f0fdf4",
  surface:       "#ffffff",
  border:        "#bbf7d0",
  border2:       "#86efac",
  text:          "#14532d",
  textSoft:      "#166534",
  muted:         "#6b7280",
  accentFg:      "#f0fdf4",
  sidebar:       "#14532d",
  sidebarHover:  "#166534",
  sidebarActive: "#15803d",
  sidebarBorder: "#166534",
  gold:          "#4ade80",
  sans:          "'Montserrat', sans-serif",
  serif:         "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }

  .desk-sidebar { display: flex !important; }
  .mob-tabs-bar { display: none; }

  @media (max-width: 767px) {
    .mob-tabs-bar { display: block; }
    .desk-sidebar { display: none !important; }
    .desk-main    { margin-left: 0 !important; }
    .desk-crumb   { display: none !important; }
    .mob-crumb    { display: flex !important; }
  }

  .cl-tabs-scroll {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 10px 14px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .cl-tabs-scroll::-webkit-scrollbar { display: none; }

  .cl-tab-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 14px;
    border-radius: 20px;
    border: 1.5px solid #bbf7d0;
    background: #f0fdf4;
    cursor: pointer;
    flex-shrink: 0;
    transition: all .18s ease;
    font-family: 'Montserrat', sans-serif;
    min-width: 62px;
  }
  .cl-tab-pill:hover  { border-color: #86efac; background: #dcfce7; }
  .cl-tab-pill.active { background: #14532d; border-color: #14532d; }

  .cl-tab-pill-icon { display: flex; align-items: center; justify-content: center; color: #6b7280; transition: color .15s; }
  .cl-tab-pill.active .cl-tab-pill-icon { color: #f0fdf4; }

  .cl-tab-pill-label { font-size: 10px; font-weight: 500; color: #166534; white-space: nowrap; letter-spacing: .02em; transition: color .15s; }
  .cl-tab-pill.active .cl-tab-pill-label { color: #f0fdf4; }

  .mob-crumb { display: none; align-items: center; gap: 6px; height: 34px; padding: 0 16px; background: #ffffff; border-bottom: 0.5px solid #bbf7d0; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .fade-up { animation: fadeUp .22s ease both; }
`;

const HEADER_H     = 56;
const SIDEBAR_FULL = 220;
const SIDEBAR_MINI = 56;

type TabKey = "current" | "past" | "reviews";

type Tab = { group: string; title: string; key: TabKey; icon: React.ReactNode };

const TABS: Tab[] = [
  { group: "Bookings", title: "Current",  key: "current",  icon: <Calendar size={15} /> },
  { group: "Bookings", title: "Past",     key: "past",     icon: <History  size={15} /> },
  { group: "Account",  title: "Reviews",  key: "reviews",  icon: <Star     size={15} /> },
];

/* ════════════════════════════════════════════════════════════ */
const CleaningBookingsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user     = useSelector((s: RootState) => s.auth.user);
  const userId   = user?.userId;

  const [active,    setActive]    = useState<TabKey>("current");
  const [collapsed, setCollapsed] = useState(false);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  const groups      = Array.from(new Set(TABS.map(t => t.group)));
  const sbW         = collapsed ? SIDEBAR_MINI : SIDEBAR_FULL;
  const activeTitle = TABS.find(t => t.key === active)?.title ?? "";

  /* scroll active pill into view on mobile */
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const renderContent = () => {
    switch (active) {
      case "current": return <CleaningBookings />;
      case "past":    return (
        <div style={{ padding: "40px 20px", textAlign: "center", color: T.muted }}>
          <span style={{ fontSize: 40 }}>🧹</span>
          <p style={{ marginTop: 12, fontWeight: 600, color: T.textSoft }}>Past cleanings coming soon</p>
        </div>
      );
      case "reviews": return <CleaningReviewPreview />;
      default:        return <CleaningBookings />;
    }
  };

  /* ── sidebar nav item ── */
  const NavItem = ({ tab }: { tab: Tab }) => {
    const isActive = active === tab.key;
    return (
      <button
        onClick={() => setActive(tab.key)}
        title={collapsed ? tab.title : undefined}
        style={{
          display: "flex", alignItems: "center",
          gap:           collapsed ? 0 : 10,
          justifyContent: collapsed ? "center" : "flex-start",
          width:         "100%",
          padding:       collapsed ? "10px 0" : "9px 8px",
          borderRadius:  7, border: "none", cursor: "pointer",
          background:    isActive ? T.sidebarActive : "transparent",
          fontFamily:    T.sans, marginBottom: 2,
          transition:    "background .15s",
          color:         isActive ? T.accentFg : T.muted,
        }}
        onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = T.sidebarHover; }}
        onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
      >
        <span style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          color:     isActive ? T.gold : T.muted,
          flexShrink: 0, width: 18, height: 18,
        }}>
          {tab.icon}
        </span>

        {!collapsed && (
          <span style={{ fontSize: 12, flex: 1, whiteSpace: "nowrap", fontWeight: isActive ? 500 : 400 }}>
            {tab.title}
          </span>
        )}

        {!collapsed && isActive && (
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.gold, flexShrink: 0 }} />
        )}
      </button>
    );
  };

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: T.bg, fontFamily: T.sans, color: T.text }}>
      <style>{FONT_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, height: HEADER_H,
        background: "rgba(20,83,45,.97)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid #166534",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 16px", zIndex: 300,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={logo} alt="Logo" style={{ height: 28, objectFit: "contain" }} />
          <span style={{ color: "#fff", fontSize: 13, fontWeight: 300, letterSpacing: "0.3em", fontFamily: T.serif }}>
            ARELUXE
          </span>
        </div>

        {/* badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(74,222,128,.15)", border: "1px solid rgba(74,222,128,.3)", borderRadius: 999, padding: "3px 10px" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80" }} />
            <span style={{ color: "#4ade80", fontSize: 10, fontWeight: 600, letterSpacing: "0.06em" }}>CLEANING</span>
          </div>

          {/* guest avatar */}
          <div
            onClick={() => navigate(`/profile/${userId}`)}
            style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#166534", border: "1.5px solid #4ade80",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", overflow: "hidden", flexShrink: 0,
            }}
            title="View profile"
          >
            {user?.profilePicture
              ? <img src={user.profilePicture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>{user?.firstName?.[0] || "G"}</span>
            }
          </div>
        </div>
      </header>

      {/* ── MOBILE TABS BAR ── */}
      <div
        className="mob-tabs-bar"
        style={{
          position: "fixed", top: HEADER_H, left: 0, right: 0,
          background: T.surface, borderBottom: `1px solid ${T.border}`, zIndex: 200,
        }}
      >
        <div className="cl-tabs-scroll">
          {TABS.map(({ key, title, icon }) => (
            <button
              key={key}
              ref={active === key ? activeTabRef : null}
              className={`cl-tab-pill${active === key ? " active" : ""}`}
              onClick={() => setActive(key as TabKey)}
            >
              <span className="cl-tab-pill-icon">{icon}</span>
              <span className="cl-tab-pill-label">{title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: "flex", paddingTop: HEADER_H, flex: 1 }}>

        {/* ── DESKTOP SIDEBAR ── */}
        <aside
          className="desk-sidebar"
          style={{
            position: "fixed", top: HEADER_H, left: 0, bottom: 0,
            width: sbW, background: T.sidebar,
            borderRight: `1px solid ${T.sidebarBorder}`,
            flexDirection: "column",
            transition: "width .25s ease",
            zIndex: 200, overflow: "hidden",
          }}
        >
          {/* user info box */}
          {!collapsed && (
            <div style={{ padding: "10px 12px", borderBottom: `1px solid ${T.sidebarBorder}` }}>
              <div style={{ background: "#166534", borderRadius: 6, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                  background: user?.profilePicture ? `url(${user.profilePicture}) center/cover` : "#15803d",
                  overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1.5px solid #4ade80",
                }}>
                  {!user?.profilePicture && (
                    <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>{user?.firstName?.[0] || "G"}</span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 11, color: "#f0fdf4", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0 }}>
                    {user?.firstName || "Guest"}
                  </p>
                  <p style={{ fontSize: 9, color: "#86efac", marginTop: 2, margin: 0 }}>My cleanings</p>
                </div>
              </div>
            </div>
          )}

          {/* nav groups */}
          <nav style={{ flex: 1, overflowY: "auto", padding: 8, scrollbarWidth: "none" as const }}>
            {groups.map(group => (
              <div key={group}>
                {!collapsed
                  ? <p style={{ fontSize: 9, color: "#86efac", letterSpacing: "0.13em", textTransform: "uppercase" as const, padding: "10px 8px 4px", margin: 0 }}>
                      {group}
                    </p>
                  : <div style={{ height: 10 }} />
                }
                {TABS.filter(t => t.group === group).map(tab => (
                  <NavItem key={tab.key} tab={tab} />
                ))}
              </div>
            ))}
          </nav>

          {/* collapse toggle */}
          <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, padding: 8 }}>
            <button
              onClick={() => setCollapsed(p => !p)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: 8, borderRadius: 7,
                border: "none", background: "transparent", cursor: "pointer",
                gap: collapsed ? 0 : 6,
              }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = T.sidebarHover}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = "transparent"}
            >
              {collapsed
                ? <ChevronRight style={{ fontSize: 16, color: "#86efac" }} />
                : <>
                    <ChevronLeft style={{ fontSize: 16, color: "#86efac" }} />
                    <span style={{ fontSize: 11, color: "#86efac" }}>Collapse</span>
                  </>
              }
            </button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main
          className="desk-main"
          style={{ flex: 1, marginLeft: sbW, transition: "margin-left .25s ease", minWidth: 0 }}
        >
          {/* desktop breadcrumb */}
          <div
            className="desk-crumb"
            style={{
              height: 44, background: T.surface,
              borderBottom: `0.5px solid ${T.border}`,
              display: "flex", alignItems: "center",
              gap: 6, padding: "0 24px",
            }}
          >
            <span style={{ fontSize: 11, color: T.muted }}>My cleanings</span>
            <span style={{ fontSize: 11, color: T.border2 }}>/</span>
            <span style={{ fontSize: 11, color: T.textSoft, fontWeight: 500 }}>{activeTitle}</span>
          </div>

          {/* mobile breadcrumb */}
          <div className="mob-crumb" style={{ marginTop: 66 }}>
            <span style={{ fontSize: 11, color: T.muted }}>My cleanings</span>
            <span style={{ fontSize: 11, color: T.border2 }}>/</span>
            <span style={{ fontSize: 11, color: T.textSoft, fontWeight: 500 }}>{activeTitle}</span>
          </div>

          <div className="fade-up" key={active} style={{ padding: "20px 16px 100px" }}>
            {renderContent()}
          </div>
        </main>

      </div>
    </div>
  );
};

export default CleaningBookingsDashboard;
